import { Global } from "@emotion/react";
import React from "react";
import { initKeyboardFocus } from "../util/keyboardFocus";
import { useMatchMedia } from "../util/useMatchMedia/useMatchMedia";
import { useOnMount } from "../util/useOnMount/useOnMount";
import { getPersistedThemeName } from "./storedTheme";
import { ThemeProvider } from "./styled";
import type { ThemeName } from "./styled/theme";
import { cssBaseline } from "./styled/theme/cssBaseline";
import type { CSSvariableLocations } from "./styled/theme/ThemeProvider/ThemeProvider";

interface Props {
  children: React.ReactNode;
  key?: string;
  colorScheme?: ThemeName;
  /**
   * If `true` (default), a global stylesheet will get injected
   * into the page. This should ideally only happen once, in the
   * main wrapper of your application. If you want to override
   * the theme in a specific part of your application, be sure
   * to pass `false` for this prop.
   */
  setCssBaseline?: boolean;
  cssVariableLocation?: CSSvariableLocations;
}

export const KivraTheme = ({
  key,
  children,
  colorScheme,
  setCssBaseline = true,
  cssVariableLocation = "wrapper",
}: Props): React.JSX.Element => {
  useOnMount(initKeyboardFocus);

  const theme = useKivraTheme(colorScheme);

  return (
    <ThemeProvider
      theme={theme}
      key={key}
      cssVariableLocation={cssVariableLocation}
    >
      {setCssBaseline && <Global styles={cssBaseline(theme)} />}
      {children}
    </ThemeProvider>
  );
};

function useKivraTheme(colorScheme?: ThemeName): ThemeName {
  const prefersDarkMode = useMatchMedia("(prefers-color-scheme: dark)");
  const persistedThemeName = getPersistedThemeName();

  // colorScheme as prop has 1st priority
  if (colorScheme) {
    return colorScheme;
  }

  // persistedThemeName has 2nd priority
  // this is what the user has explicitly chosen in settings
  if (persistedThemeName) {
    return persistedThemeName;
  }

  // browser preference has 3rd priority
  if (prefersDarkMode) {
    return "dark";
  }

  // default to light theme
  return "light";
}
